import {
  createRootRoute,
  createRoute,
  createRouter,
  RouterProvider,
  Outlet,
  Navigate,
  redirect,
} from '@tanstack/react-router';
import { Header } from './components/Header.tsx';
import { BottomNav } from './components/BottomNav.tsx';
import { Sidebar } from './components/Sidebar.tsx';
import { TopBar } from './components/TopBar.tsx';
import { Login } from './pages/Login.tsx';
import { AcceptInvite } from './pages/AcceptInvite.tsx';
import { ForgotPassword } from './pages/ForgotPassword.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { Profile } from './pages/Profile.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Landing } from './pages/Landing.tsx';
import { Applications } from './pages/Applications.tsx';
import { Security } from './pages/Security.tsx';
import { Team } from './pages/Team.tsx';
import { AdminOrgs } from './pages/AdminOrgs.tsx';
import { AdminOrgDetail } from './pages/AdminOrgDetail.tsx';
import { AdminSubscriptions } from './pages/AdminSubscriptions.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { useAuthStore } from './store/auth.ts';

function RootLayout(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  return (
    <div className={`min-h-screen flex flex-col ${user ? 'bg-[hsl(var(--chrome))]' : ''}`}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <TopBar />
      <Sidebar />
      {/* Fixed inverted-corner masks at the top of the content area, so the white
          panel's rounded corners stay put as the page scrolls (they're not on the
          scrolling content). Each paints only the chrome-coloured notch. */}
      {user && (
        <div
          aria-hidden
          className="hidden md:block fixed top-16 left-64 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom right, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      {user && (
        <div
          aria-hidden
          className="hidden md:block fixed top-16 right-0 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom left, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      <Header />
      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-1 ${user ? 'has-bottom-nav md:ml-64 md:pt-16' : ''}`}
      >
        {user ? (
          <div className="min-h-dvh bg-background md:min-h-[calc(100dvh-4rem)]">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      <BottomNav />
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const requireAuth = (): void => {
  if (!useAuthStore.getState().accessToken) throw redirect({ to: '/login' });
};

const requireOrgAdmin = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (state.user?.role !== 'ORG_ADMIN') throw redirect({ to: '/dashboard' });
};

const requireSysAdmin = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (state.user?.role !== 'SYS_ADMIN') throw redirect({ to: '/dashboard' });
};

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: Login });
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: AcceptInvite,
});
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPassword,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password/$token',
  component: ResetPassword,
});

function LandingOrRedirect(): JSX.Element {
  // Anonymous visitors get the public marketing landing; signed-in users are
  // sent straight to their app home (unchanged behavior).
  if (!useAuthStore.getState().accessToken) return <Landing />;
  const role = useAuthStore.getState().user?.role;
  return <Navigate to={role === 'SYS_ADMIN' ? '/admin/orgs' : '/dashboard'} />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingOrRedirect,
});
const applicationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications',
  component: Applications,
});
const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security',
  component: Security,
});
const dashRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  beforeLoad: requireAuth,
  component: Dashboard,
});
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  beforeLoad: requireAuth,
  component: Profile,
});
const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/team',
  beforeLoad: requireOrgAdmin,
  component: Team,
});
const adminOrgsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/orgs',
  beforeLoad: requireSysAdmin,
  component: AdminOrgs,
});
const adminOrgDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/orgs/$id',
  beforeLoad: requireSysAdmin,
  component: AdminOrgDetail,
});
const adminSubscriptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/subscriptions',
  beforeLoad: requireSysAdmin,
  component: AdminSubscriptions,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  inviteRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  indexRoute,
  applicationsRoute,
  securityRoute,
  dashRoute,
  profileRoute,
  teamRoute,
  adminOrgsRoute,
  adminOrgDetailRoute,
  adminSubscriptionsRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter(): JSX.Element {
  return <RouterProvider router={router} />;
}
